import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { T, useGT } from 'gt-react';
import type { WorkbookCell } from '@erp/shared-types';
import {
  cellAddress,
  columnName,
  parseCellAddress,
  rangeHolds,
  type CellPosition,
  type CellRange,
} from '@erp/workbooks';
import { cn } from '@/lib/utils';
import { WorkbookCellValue, workbookCellFont } from '../WorkbookCellValue';
import {
  freeformGridKeyAction,
  freeformGridSize,
  freeformShownCell,
  moveCell,
  type FreeformMove,
} from './freeformGridModel';

export interface FreeformGridProps {
  /** Every filled cell's input, by A1 address, as users read it (other tables by name). */
  inputs: Readonly<Record<string, string>>;
  /** The shown Result's computed cells, by A1 address. */
  values?: Readonly<Record<string, WorkbookCell>>;
  /** The declared Result table's range: its first row is shaded as the header. */
  table: CellRange | null;
  selected: string;
  onSelect: (address: string) => void;
  /** Commits what was typed into a cell ('' clears it). */
  onCommit: (address: string, input: string) => void;
  readOnly: boolean;
  /** The shown Result is Stale: computed cells are muted until a rerun. */
  stale: boolean;
}

/**
 * A freeform sheet's grid, as a spreadsheet's: column letters and row numbers,
 * a selected cell moved by arrow keys, typing or F2 or double-click editing
 * it in place (Enter commits and moves down, Tab right, Escape cancels), and
 * Delete clearing it.
 */
export function FreeformGrid(props: FreeformGridProps) {
  const gt = useGT();
  const grid = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState<{
    address: string;
    draft: string;
  } | null>(null);
  const { rows, columns } = freeformGridSize(props.inputs, props.table);
  const selected = parseCellAddress(props.selected) ?? { column: 1, row: 1 };

  function select(position: CellPosition) {
    props.onSelect(cellAddress(position));
  }
  // Set once an edit closes, so the editor's blur as focus returns to the
  // grid neither commits a cancelled draft nor commits twice.
  const closing = useRef(false);
  function startEditing(draft: string) {
    if (props.readOnly) return;
    closing.current = false;
    setEditing({ address: props.selected, draft });
  }
  function cancel() {
    closing.current = true;
    setEditing(null);
    grid.current?.focus();
  }
  function commit(move?: FreeformMove) {
    if (closing.current) return;
    closing.current = true;
    if (editing) props.onCommit(editing.address, editing.draft);
    setEditing(null);
    if (move) select(moveCell(selected, move, { rows, columns }));
    grid.current?.focus();
  }
  function onGridKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (editing) return;
    const action = freeformGridKeyAction(event);
    if (!action) return;
    event.preventDefault();
    if (action.kind === 'move')
      select(moveCell(selected, action.move, { rows, columns }));
    else if (action.kind === 'edit')
      startEditing(action.text ?? props.inputs[props.selected] ?? '');
    else if (!props.readOnly) props.onCommit(props.selected, '');
  }
  function onEditorKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // Enter confirms an IME candidate while composing; it must not commit.
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(event.shiftKey ? 'up' : 'down');
    } else if (event.key === 'Tab') {
      event.preventDefault();
      commit(event.shiftKey ? 'left' : 'right');
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
    }
  }

  return (
    <div
      ref={grid}
      role="grid"
      tabIndex={0}
      aria-label={gt('Freeform sheet cells', { context: 'freeform grid' })}
      aria-rowcount={rows}
      aria-colcount={columns}
      onKeyDown={onGridKeyDown}
      className="max-h-[70vh] overflow-auto rounded-md border outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <table className="border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-10">
          <tr>
            <th className="sticky left-0 z-20 w-10 border-r border-b bg-muted">
              <span className="sr-only">
                <T context="freeform grid corner header above the row numbers">
                  Row
                </T>
              </span>
            </th>
            {Array.from({ length: columns }, (_, index) => (
              <th
                key={index}
                scope="col"
                className={cn(
                  'min-w-24 border-r border-b bg-muted px-2 py-1 text-xs font-medium text-muted-foreground',
                  selected.column === index + 1 && 'bg-accent text-foreground',
                )}
              >
                {columnName(index + 1)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, rowIndex) => {
            const row = rowIndex + 1;
            return (
              <tr key={row}>
                <th
                  scope="row"
                  className={cn(
                    'sticky left-0 z-10 border-r border-b bg-muted px-2 text-right text-xs font-medium text-muted-foreground tabular-nums',
                    selected.row === row && 'bg-accent text-foreground',
                  )}
                >
                  {row}
                </th>
                {Array.from({ length: columns }, (_, columnIndex) => {
                  const position = { column: columnIndex + 1, row };
                  const address = cellAddress(position);
                  const isSelected = address === props.selected;
                  const inTable =
                    props.table !== null && rangeHolds(props.table, position);
                  const isHeader = inTable && props.table?.start.row === row;
                  return (
                    <td
                      key={address}
                      role="gridcell"
                      aria-selected={isSelected}
                      data-cell={address}
                      onClick={() => select(position)}
                      onDoubleClick={() =>
                        startEditing(props.inputs[address] ?? '')
                      }
                      className={cn(
                        'h-7 max-w-64 min-w-24 cursor-cell truncate border-r border-b px-2 py-0.5',
                        inTable && 'bg-muted/40',
                        isHeader && 'font-medium',
                        isSelected &&
                          'outline-2 -outline-offset-2 outline-primary',
                      )}
                    >
                      {editing?.address === address ? (
                        <CellEditor
                          address={address}
                          draft={editing.draft}
                          onChange={(draft) => setEditing({ address, draft })}
                          onKeyDown={onEditorKeyDown}
                          onBlur={() => commit()}
                        />
                      ) : (
                        <CellContent
                          input={props.inputs[address]}
                          value={props.values?.[address]}
                          stale={props.stale}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CellContent({
  input,
  value,
  stale,
}: {
  input: string | undefined;
  value: WorkbookCell | undefined;
  stale: boolean;
}) {
  const shown = freeformShownCell(input, value);
  if (shown.kind === 'empty') return null;
  if (shown.kind === 'formula')
    return (
      <span className="font-mono text-xs text-muted-foreground">
        {shown.text}
      </span>
    );
  return (
    <span
      className={cn(
        'block truncate',
        workbookCellFont(shown.type),
        shown.computed && stale && 'opacity-60',
      )}
    >
      <WorkbookCellValue cell={shown.cell} type={shown.type} />
    </span>
  );
}

/** The input a cell is edited in, focused as it opens. */
function CellEditor({
  address,
  draft,
  onChange,
  onKeyDown,
  onBlur,
}: {
  address: string;
  draft: string;
  onChange: (draft: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onBlur: () => void;
}) {
  const gt = useGT();
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
  }, []);
  return (
    <input
      ref={input}
      aria-label={gt('Cell {address}', {
        context: 'freeform cell editor',
        address,
      })}
      className="h-6 w-full min-w-40 bg-background font-mono text-sm outline-none"
      value={draft}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    />
  );
}
